import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    ArrowLeftIcon,
    ChevronDownIcon,
    ChevronRightIcon,
    CubeIcon,
    MagnifyingGlassIcon,
    PlusIcon,
    SparklesIcon,
    TrashIcon,
} from "@heroicons/react/24/outline";
import { toast } from "react-toastify";
import Drawer from "../Drawer";
import { ConfrimModal } from "../ConfrimModal";
import { LoaderSpinner } from "../LoaderSpinner";
import { AddDrawerSelect } from "../productCatalog/AddDrawerSelect";
import CatalogStatusChip, {
    catalogStatusLabel,
    normalizeCatalogStatus,
} from "../productCatalog/CatalogStatusChip";
import SupplySku from "../SupplySku";
import BomToolbar from "./BomToolbar";
import BomBulkBar from "./BomBulkBar";
import BomTable, { bomColumnRenderers } from "./BomTable";
import EditProductBomDrawer from "./EditProductBomDrawer";
import CopyBomPicker from "./CopyBomPicker";
import { downloadCsv } from "./bomExport";
import { useDebounce } from "../../helpers/useDebounce";
import { enumInventoryStatuses } from "../../consts/inventoryStatus";
import {
    addBomProduct,
    BomUiLoadingKeys,
    clearBomFilters,
    clearBomRowSelection,
    createBomComponentLibraryItem,
    deleteAllProductBom,
    fetchBomComponentLibrary,
    fetchProductBomUi,
    saveProductBom,
    saveProductBomBulk,
    selectBomUi,
    setBomExpandedRows,
    setBomFilter,
    setBomSettingsOpen,
    setBomRowSelection,
    searchBomProducts,
    toggleBomExpandedRow,
    toggleBomRowSelection,
    updateBomProduct,
} from "../../slices/bomUiSlice";
import { fetchProductCatalogCategorySettings, updateProductCatalogProductSettings } from "../../slices/productCatalogSlice";
import { ProductGroupLoadingKeys, getSuppliers } from "../../slices/productGroupSlice";
import { updateVendorByProduct } from "../../slices/productsSlice";
import { arrayHasItems } from "../../helpers/common";

const PRODUCT_BOM_SUPPLIER_STACK = (lineId) => `product-bom-line-supplier-${lineId}`;

function formatInventoryStatusLabel(status) {
    const raw = String(status || "").trim();
    return enumInventoryStatuses?.[raw] || raw || "Active";
}

const bomCurrencyFormatter = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
});

function formatMoney(value) {
    return bomCurrencyFormatter.format(Number(value || 0));
}

function blankComponentLine() {
    return {
        id: `line-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
        componentName: "",
        componentType: "",
        description: "",
        supplier: "",
        vendorId: "",
        componentPresetId: "",
        qty: 1,
        unitCost: 0,
        extendedCost: 0,
        isNewComponent: false,
    };
}

function mapSavedComponentToLine(c, index) {
    const qty = Number(c.qty ?? 1);
    const unitCost = Number(c.unitCost ?? 0);
    const vendorId =
        c.vendorId != null && c.vendorId !== ""
            ? String(c.vendorId)
            : c.vendor_id != null && c.vendor_id !== ""
              ? String(c.vendor_id)
              : "";
    return {
        id: c.id || `line-${Date.now()}-${index}`,
        componentName: String(c.componentName || "").trim(),
        componentType: String(c.componentType || "").trim(),
        description: String(c.description || "").trim(),
        supplier: String(c.supplier || "").trim(),
        vendorId,
        packagingGroupId:
            c.packagingGroupId != null && c.packagingGroupId !== ""
                ? String(c.packagingGroupId)
                : c.packaging_group_id != null && c.packaging_group_id !== ""
                  ? String(c.packaging_group_id)
                  : "",
        componentPresetId:
            c.componentPresetId != null && c.componentPresetId !== ""
                ? String(c.componentPresetId)
                : c.component_preset_id != null && c.component_preset_id !== ""
                  ? String(c.component_preset_id)
                  : "",
        qty,
        unitCost,
        extendedCost: Math.round(qty * unitCost * 10000) / 10000,
        isNewComponent: false,
    };
}

function mapCatalogResultToProduct(item, index) {
    const supplySku = String(item?.supplySku || item?.supply_sku || item?.sku || "").trim().toUpperCase();
    const canonicalName = String(
        item?.canonicalName || item?.canonical_name || item?.productName || item?.product_name || item?.name || ""
    ).trim();
    return {
        id: item?.id || `catalog-sku-${supplySku || index}`,
        supplySku,
        canonicalName,
        productName: canonicalName,
        category: String(item?.category || item?.category_name || "").trim(),
        supplier: String(item?.supplier || item?.primary_supplier || item?.vendor || item?.vendor_name || "").trim(),
        leadTimeDays: item?.leadTimeDays ?? item?.lead_time_days ?? item?.leadTime ?? item?.lead_time ?? "",
        status: formatInventoryStatusLabel(item?.status),
    };
}

const BULK_BOX_PRESET_REPLACED_COMPONENTS = new Set(["box", "insert", "sleeve"]);

function createBulkDraftComponentLine(draft, seed = "") {
    const qty = Number(draft?.qty ?? 1);
    const unitCost = Number(draft?.unitCost ?? 0);
    return {
        id: `bulk-line-${seed || Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
        componentName: String(draft?.componentName || "").trim(),
        componentType: String(draft?.componentType || "").trim(),
        description: String(draft?.description || "").trim(),
        supplier: String(draft?.supplier || "").trim(),
        vendorId:
            draft?.vendorId != null && String(draft.vendorId).trim() !== "" ? String(draft.vendorId).trim() : "",
        componentPresetId:
            draft?.componentPresetId != null && String(draft.componentPresetId).trim() !== ""
                ? String(draft.componentPresetId).trim()
                : draft?.component_preset_id != null && String(draft.component_preset_id).trim() !== ""
                  ? String(draft.component_preset_id).trim()
                  : "",
        qty,
        unitCost,
        extendedCost: Math.round(qty * unitCost * 10000) / 10000,
        isNewComponent: false,
    };
}

function createBoxPresetLines(preset) {
    return (preset?.components || []).map((component, index) => {
        const qty = Number(component?.qty ?? 1);
        const unitCost = Number(component?.unitCost ?? 0);
        return {
            id: `bulk-preset-${preset?.id || "preset"}-${index}-${Math.random().toString(36).slice(2, 8)}`,
            componentName: String(component?.component || component?.componentName || component?.name || "").trim(),
            componentType: "Packaging",
            description:
                String(component?.description || "").trim() ||
                `${String(preset?.name || "").trim()} — ${String(component?.component || component?.componentName || component?.name || "").trim()}`,
            supplier: String(component?.supplier || "").trim(),
            vendorId:
                component?.vendorId != null && String(component.vendorId).trim() !== ""
                    ? String(component.vendorId).trim()
                    : component?.vendor_id != null && String(component.vendor_id).trim() !== ""
                      ? String(component.vendor_id).trim()
                      : "",
            componentPresetId: "",
            qty,
            unitCost,
            extendedCost: Math.round(qty * unitCost * 10000) / 10000,
            isNewComponent: false,
        };
    });
}

function serializeProductBomComponents(components = []) {
    return components
        .filter((component) => String(component?.componentName || component?.component || "").trim())
        .map((component) => ({
            component: String(component?.componentName || component?.component || "").trim(),
            description: String(component?.description || "").trim() || null,
            qty: Number(component?.qty) || 0,
            cost: Number(component?.unitCost ?? component?.cost ?? 0) || 0,
            vendorId:
                component?.vendorId != null && String(component.vendorId).trim() !== ""
                    ? String(component.vendorId).trim()
                    : component?.vendor_id != null && String(component.vendor_id).trim() !== ""
                      ? String(component.vendor_id).trim()
                      : null,
            packaging_group_id:
                component?.packagingGroupId != null && String(component.packagingGroupId).trim() !== ""
                    ? String(component.packagingGroupId).trim()
                    : component?.packaging_group_id != null && String(component.packaging_group_id).trim() !== ""
                      ? String(component.packaging_group_id).trim()
                      : null,
            component_preset_id:
                component?.componentPresetId != null && String(component.componentPresetId).trim() !== ""
                    ? String(component.componentPresetId).trim()
                    : component?.component_preset_id != null && String(component.component_preset_id).trim() !== ""
                      ? String(component.component_preset_id).trim()
                      : null,
        }));
}

function ProductBomDrawer({
    isOpen,
    onClose,
    componentLibrary,
    boxPresets,
    onSave,
    vendorOptions = [],
    vendorSelectLoading = false,
    saveBusy = false,
    productBomRows = [],
}) {
    const dispatch = useDispatch();
    const [step, setStep] = useState(1);
    const [skuSearch, setSkuSearch] = useState("");
    const debouncedSkuSearch = useDebounce(skuSearch, 500);
    const [skuResults, setSkuResults] = useState([]);
    const [skuSearchLoading, setSkuSearchLoading] = useState(false);
    const [skuSearchError, setSkuSearchError] = useState("");
    const [selectedProducts, setSelectedProducts] = useState([]);
    const [lines, setLines] = useState([blankComponentLine()]);
    const [openDropdown, setOpenDropdown] = useState(null);
    const [dropdownSearch, setDropdownSearch] = useState("");
    const [openSupplierSelectKey, setOpenSupplierSelectKey] = useState(null);
    const [copySourceId, setCopySourceId] = useState("");
    const dropdownRef = useRef(null);

    useEffect(() => {
        const handler = (e) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
                setOpenDropdown(null);
                setDropdownSearch("");
            }
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, []);

    useEffect(() => {
        if (!isOpen) return;
        setStep(1);
        setSkuSearch("");
        setSkuResults([]);
        setSkuSearchLoading(false);
        setSkuSearchError("");
        setSelectedProducts([]);
        setLines([blankComponentLine()]);
        setOpenDropdown(null);
        setDropdownSearch("");
        setOpenSupplierSelectKey(null);
        setCopySourceId("");
    }, [isOpen]);

    useEffect(() => {
        const q = String(debouncedSkuSearch || "").trim();
        if (!isOpen || step !== 1 || !q) {
            setSkuResults([]);
            setSkuSearchLoading(false);
            setSkuSearchError("");
            return;
        }

        let cancelled = false;
        const run = async () => {
            setSkuSearchLoading(true);
            setSkuSearchError("");
            try {
                const data = await dispatch(searchBomProducts({ search: q, limit: 12, excludeByBOMType: true })).unwrap();
                if (cancelled) return;
                const rows = (data?.items || [])
                    .map((item, index) => mapCatalogResultToProduct(item, index))
                    .filter((item) => item.supplySku || item.canonicalName);
                setSkuResults(rows);
            } catch (error) {
                if (cancelled) return;
                setSkuResults([]);
                const msg =
                    typeof error === "string"
                        ? error
                        : error?.message || "Failed to search BOM products";
                setSkuSearchError(String(msg));
            } finally {
                if (!cancelled) {
                    setSkuSearchLoading(false);
                }
            }
        };

        run();
        return () => {
            cancelled = true;
        };
    }, [debouncedSkuSearch, dispatch, isOpen, step]);

    const filteredLibrary = useMemo(() => {
        const q = dropdownSearch.trim().toLowerCase();
        const lib = componentLibrary || [];
        if (!q) return lib.slice(0, 25);
        return lib
            .filter((c) => {
                const hay = [c.name, c.type, c.notes, c.defaultSupplier].filter(Boolean).join(" ").toLowerCase();
                return hay.includes(q);
            })
            .slice(0, 25);
    }, [componentLibrary, dropdownSearch]);

    const filteredBoxPresets = useMemo(() => {
        const q = dropdownSearch.trim().toLowerCase();
        const presets = boxPresets || [];
        if (!q) return presets.slice(0, 15);
        return presets
            .filter((p) => {
                const hay = [p.name, p.notes, ...(p.components || []).map((c) => c.component)]
                    .filter(Boolean)
                    .join(" ")
                    .toLowerCase();
                return hay.includes(q);
            })
            .slice(0, 15);
    }, [boxPresets, dropdownSearch]);

    const dropdownSearchMatchesLibrary =
        dropdownSearch.trim().length > 0 &&
        (componentLibrary || []).some((c) => String(c.name || "").toLowerCase() === dropdownSearch.trim().toLowerCase());

    const updateLine = (id, patch) => {
        setLines((prev) =>
            prev.map((l) => {
                if (l.id !== id) return l;
                const next = { ...l, ...patch };
                next.extendedCost = Math.round(Number(next.qty || 0) * Number(next.unitCost || 0) * 10000) / 10000;
                return next;
            })
        );
    };

    const selectLibraryComponent = (lineId, comp) => {
        const vid =
            comp.vendorId != null && comp.vendorId !== ""
                ? String(comp.vendorId)
                : comp.vendor_id != null && comp.vendor_id !== ""
                  ? String(comp.vendor_id)
                  : "";
        updateLine(lineId, {
            componentName: comp.name,
            componentType: comp.type || "",
            description: comp.type || comp.description || "",
            supplier: comp.defaultSupplier || "",
            vendorId: vid,
            componentPresetId:
                comp.componentPresetId != null && comp.componentPresetId !== ""
                    ? String(comp.componentPresetId)
                    : comp.id != null && comp.id !== ""
                      ? String(comp.id)
                      : "",
            qty: Number(comp.defaultQty ?? 1),
            unitCost: Number(comp.defaultUnitCost ?? 0),
            isNewComponent: false,
        });
        setOpenDropdown(null);
        setDropdownSearch("");
    };

    const selectBoxPreset = (lineId, preset) => {
        const presetComponents = preset.components || [];
        const packagingExtendedTotal = arrayHasItems(presetComponents)
            ? Math.round(
                  presetComponents.reduce(
                      (sum, pc) =>
                          sum + Number(pc.qty ?? 1) * Number(pc.unitCost ?? 0),
                      0
                  ) * 10000
              ) / 10000
            : 0;
        const packagingLine = {
            id: `line-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
            componentName: preset.name,
            componentType: "Packaging",
            description: "Packaging",
            packagingGroupId: preset.groupId,
            qty: arrayHasItems(presetComponents) ? 1 : 0,
            unitCost: packagingExtendedTotal,
            extendedCost: packagingExtendedTotal,
            componentPresetId: "",
            isNewComponent: false,
        };
        setLines((prev) => {
            const idx = prev.findIndex((l) => l.id === lineId);
            if (idx === -1) return [...prev, packagingLine];
            const next = [...prev];
            next.splice(idx, 1, packagingLine);
            return next;
        });
        setOpenDropdown(null);
        setDropdownSearch("");
    };

    const selectNewComponent = (lineId, name) => {
        updateLine(lineId, {
            componentName: name,
            componentType: "",
            description: "",
            supplier: "",
            vendorId: "",
            componentPresetId: "",
            qty: 1,
            unitCost: 0,
            isNewComponent: true,
        });
        setOpenDropdown(null);
        setDropdownSearch("");
    };

    const addLine = () => setLines((prev) => [...prev, blankComponentLine()]);
    const removeLine = (id) => setLines((prev) => prev.filter((l) => l.id !== id));

    const handleToggleExisting = (p) => {
        const id = String(p.id || "").trim();
        if (!id) return;
        setSelectedProducts((previous) =>
            previous.some((item) => String(item.id) === id)
                ? previous.filter((item) => String(item.id) !== id)
                : [...previous, p]
        );
    };

    const hasSingleSelectedProduct = selectedProducts.length === 1;
    const selectedProductIds = selectedProducts.map((product) => String(product.id || "").trim()).filter(Boolean);
    const singleSelectedProduct = hasSingleSelectedProduct ? selectedProducts[0] : null;
    const singleTargetId = String(singleSelectedProduct?.id || "");
    const singleTargetSku = String(singleSelectedProduct?.supplySku || "").trim().toUpperCase();
    const singleTargetName = String(
        singleSelectedProduct?.canonicalName || singleSelectedProduct?.productName || ""
    ).trim();
    const singleTargetCategory = String(singleSelectedProduct?.category || "").trim();
    const singleTargetSupplier = String(singleSelectedProduct?.supplier || "").trim();
    const singleTargetLeadTime = String(
        singleSelectedProduct?.leadTimeDays != null
            ? singleSelectedProduct.leadTimeDays
            : singleSelectedProduct?.leadTime ?? ""
    );
    const singleTargetStatus = String(singleSelectedProduct?.status || "Active");
    const singleTargetStatusCode = normalizeCatalogStatus(
        singleSelectedProduct?.status_code || singleSelectedProduct?.status
    );

    const handleContinue = () => {
        if (!selectedProducts.length) {
            toast.error("Select at least one catalog product", { position: "bottom-right" });
            return;
        }
        setStep(2);
    };

    const handleCopySource = (source) => {
        setCopySourceId(String(source?.id || ""));
        setLines(
            (source?.components || []).map((component, index) => ({
                ...mapSavedComponentToLine(component, index),
                id: `copied-line-${Date.now()}-${index}-${Math.random().toString(36).slice(2, 7)}`,
            }))
        );
    };

    const totalCost = lines.reduce((sum, l) => sum + (l.extendedCost || 0), 0);
    const newComponentCount = lines.filter((l) => l.componentName.trim() && l.isNewComponent).length;

    const handleSave = async () => {
        if (!selectedProducts.length) {
            toast.error("Select at least one catalog product first", { position: "bottom-right" });
            return;
        }
        const validLines = lines.filter((l) => String(l.componentName || "").trim());
        if (validLines.length === 0) {
            toast.error("Add at least one component", { position: "bottom-right" });
            return;
        }

        const persistableProductIds = selectedProductIds.filter((id) => !id.startsWith("product-"));
        if (!hasSingleSelectedProduct && !persistableProductIds.length) {
            toast.error("No persistable catalog products are selected", { position: "bottom-right" });
            return;
        }
        const normalizedSku = String(singleTargetSku || "").trim().toUpperCase();
        const payload = {
            id: hasSingleSelectedProduct ? singleTargetId : "",
            productIds: hasSingleSelectedProduct ? selectedProductIds : persistableProductIds,
            supplySku: hasSingleSelectedProduct ? normalizedSku : "",
            canonicalName: hasSingleSelectedProduct ? singleTargetName || normalizedSku : "",
            category: hasSingleSelectedProduct ? singleTargetCategory : "",
            supplier: hasSingleSelectedProduct ? singleTargetSupplier : "",
            status: hasSingleSelectedProduct ? singleTargetStatus || "Draft" : "",
            leadTimeDays: hasSingleSelectedProduct ? Number(singleTargetLeadTime || 0) : 0,
            totalBomCost: validLines.reduce((s, l) => s + Number(l.qty || 0) * Number(l.unitCost || 0), 0),
            componentCount: validLines.length,
            components: validLines.map((l, index) => ({
                id: l.id || `component-${Date.now()}-${index}`,
                componentSku: "",
                componentName: String(l.componentName || "").trim(),
                description: String(l.description || "").trim(),
                componentType: String(l.componentType || "").trim(),
                supplier: String(l.supplier || "").trim(),
                vendorId: l.vendorId ? String(l.vendorId) : "",
                packagingGroupId: l.packagingGroupId || null,
                componentPresetId: l.componentPresetId || null,
                qty: Number(l.qty || 0),
                unitCost: Number(l.unitCost || 0),
                extendedCost: Number(l.qty || 0) * Number(l.unitCost || 0),
            })),
        };

        try {
            await onSave(payload, { newComponents: validLines.filter((l) => l.isNewComponent) });
            onClose();
        } catch {
            /* error surfaced in parent */
        }
    };

    const handleClose = () => {
        onClose();
    };

    const showStep1 = step === 1;
    const showStep2 = step === 2;

    const footer = (
        <div className="flex-shrink-0 border-t border-gray-200 bg-white px-5 py-3.5">
            {showStep1 ? (
                <div className="flex items-center justify-between gap-3">
                    <button
                        type="button"
                        onClick={handleClose}
                        className="inline-flex h-8 items-center rounded-md px-3 text-sm text-gray-500 hover:bg-gray-50"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={handleContinue}
                        disabled={!selectedProducts.length}
                        className="inline-flex h-8 items-center gap-1 rounded-md bg-gray-900 px-4 text-sm font-medium text-white hover:bg-gray-800 disabled:cursor-not-allowed disabled:opacity-40"
                    >
                        Continue with {selectedProducts.length || 0} SKU{selectedProducts.length === 1 ? "" : "s"}
                        <ChevronRightIcon className="h-3.5 w-3.5" />
                    </button>
                </div>
            ) : (
                <div className="flex items-center justify-between gap-3">
                    <button
                        type="button"
                        onClick={handleClose}
                        className="inline-flex h-8 items-center rounded-md px-3 text-sm text-gray-500 hover:bg-gray-50"
                    >
                        Cancel
                    </button>
                    <div className="flex items-center gap-3">
                        {totalCost > 0 ? (
                            <span className="text-sm text-gray-500">
                                Total: <span className="font-semibold text-gray-900">${totalCost.toFixed(2)}</span>
                            </span>
                        ) : null}
                        <button
                            type="button"
                            onClick={() => void handleSave()}
                            disabled={saveBusy || !lines.some((l) => l.componentName.trim())}
                            className="inline-flex relativeh-8 items-center gap-1 rounded-md py-2 bg-gray-900 px-4 text-sm font-medium text-white hover:bg-gray-800 disabled:opacity-40"
                        >
                            {saveBusy && (
                                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-50">
                                    <LoaderSpinner width="14" height="14" colorSpinner="white" border="2" />
                                </div>
                            )}
                            <ChevronRightIcon className="h-3.5 w-3.5" />
                            <span>Create BOM for {selectedProducts.length} SKU{selectedProducts.length === 1 ? "" : "s"}</span>
                        </button>
                    </div>
                </div>
            )}
        </div>
    );

    return (
        <Drawer isOpen={isOpen} onClose={handleClose} width="672px" footer={footer}>
            <div className="flex min-h-0 flex-col">
                {showStep1 ? (
                    <>
                        <div className="flex-shrink-0 border-b border-gray-200 px-5 pb-4 pt-5 pr-12">
                            <div className="mb-2">
                                <span className="inline-flex items-center rounded-md border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-xs font-medium uppercase tracking-wider text-emerald-700">
                                    Product BOM
                                </span>
                            </div>
                            <h2 className="text-sm font-semibold text-gray-900">Add Product BOM</h2>
                            <p className="mt-0.5 text-sm text-gray-500">
                                Search for a SKU from the catalog. Product details will auto-populate.
                            </p>
                        </div>
                        <div className="min-h-0 flex-1 px-5 py-5">
                            <div className="relative mb-1">
                                <MagnifyingGlassIcon className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" />
                                <input
                                    autoFocus
                                    value={skuSearch}
                                    onChange={(e) => setSkuSearch(e.target.value)}
                                    placeholder="Search SKU or product name…"
                                    className="h-9 w-full rounded-md border border-gray-200 py-2 pl-8 pr-8 text-sm outline-none focus:border-gray-400"
                                />
                            </div>
                            {skuSearchLoading ? (
                                <div className="px-3 py-4">
                                    <LoaderSpinner
                                        width="30"
                                        height="30"
                                        border="3"
                                        colorSpinner="black"
                                    />
                                </div>
                            ) : null}
                            {skuSearch.trim() && !skuSearchLoading ? (
                                <div className={`mt-2 overflow-hidden rounded-md bg-white ${skuResults.length > 0 ? "border border-gray-200 shadow-sm" : ""}`}>
                                    {skuResults.length > 0 && !skuSearchLoading ? (
                                        <div>
                                            <div className="border-b border-gray-100 bg-gray-50 px-3 py-1.5">
                                                <div className="flex items-center justify-between">
                                                    <span className="text-xs font-semibold uppercase tracking-wider text-gray-400">
                                                        Catalog Products
                                                    </span>
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            const resultIds = new Set(skuResults.map((item) => String(item.id)));
                                                            setSelectedProducts((previous) => [
                                                                ...previous.filter((item) => !resultIds.has(String(item.id))),
                                                                ...skuResults,
                                                            ]);
                                                        }}
                                                        className="text-xs font-medium text-blue-600 hover:text-blue-800"
                                                    >
                                                        Select all results
                                                    </button>
                                                </div>
                                            </div>
                                            {skuResults.map((p) => (
                                                <button
                                                    key={p.id || p.supplySku}
                                                    type="button"
                                                    onClick={() => handleToggleExisting(p)}
                                                    className="group flex w-full items-center gap-3 border-b border-gray-100 px-3 py-2.5 text-left transition-colors last:border-0 hover:bg-gray-50"
                                                >
                                                    <span className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border ${
                                                        selectedProducts.some((item) => String(item.id) === String(p.id))
                                                            ? "border-blue-600 bg-blue-600"
                                                            : "border-gray-300 bg-white"
                                                    }`}>
                                                        {selectedProducts.some((item) => String(item.id) === String(p.id)) ? (
                                                            <span className="text-[10px] text-white">✓</span>
                                                        ) : null}
                                                    </span>
                                                    <SupplySku sku={p.supplySku} className="shrink-0" />
                                                    <div className="min-w-0 flex-1">
                                                        <span className="block truncate text-sm text-gray-900">
                                                            {p.canonicalName || p.productName}
                                                        </span>
                                                        <span className="text-xs text-gray-400">
                                                            {p.category} · {p.supplier}
                                                        </span>
                                                    </div>
                                                    <ChevronRightIcon className="h-3.5 w-3.5 shrink-0 text-gray-300 transition-colors group-hover:text-gray-500" />
                                                </button>
                                            ))}
                                        </div>
                                    ) : null}
                                    {skuResults.length === 0 && !skuSearchLoading ? (
                                        <div className="px-3 py-4 text-center text-sm text-gray-400">
                                            No catalog products without BOM found for &quot;{skuSearch}&quot;
                                        </div>
                                    ) : null}
                                </div>
                            ) : null}
                            {skuSearchError && !skuSearchLoading ? (
                                <p className="mt-2 text-xs text-amber-600">
                                    Could not fetch products without BOM.
                                </p>
                            ) : null}

                            {selectedProducts.length > 0 ? (
                                <div className="mt-3 rounded-md border border-blue-100 bg-blue-50/50 p-2">
                                    <div className="mb-1 flex items-center justify-between">
                                        <span className="text-xs font-semibold uppercase tracking-wider text-blue-700">
                                            {selectedProducts.length} SKU{selectedProducts.length === 1 ? "" : "s"} selected
                                        </span>
                                        <button type="button" onClick={() => setSelectedProducts([])} className="text-xs text-blue-600 underline">
                                            Clear
                                        </button>
                                    </div>
                                    <div className="flex flex-wrap gap-1.5">
                                        {selectedProducts.map((product) => (
                                            <button
                                                type="button"
                                                key={product.id}
                                                onClick={() =>
                                                    setSelectedProducts((previous) =>
                                                        previous.filter((item) => String(item.id) !== String(product.id))
                                                    )
                                                }
                                                className="inline-flex items-center gap-1 px-1 py-1"
                                            >
                                                <SupplySku sku={product.supplySku || product.sku} />
                                                <span>×</span>
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            ) : null}
                            {!skuSearch.trim() ? (
                                <div className="mt-8 text-center">
                                    <div className="mx-auto mb-3 flex h-10 w-10 items-center justify-center rounded-md bg-gray-100">
                                        <MagnifyingGlassIcon className="h-4 w-4 text-gray-400" />
                                    </div>
                                    <p className="text-sm text-gray-400">
                                        Type a SKU like <span className="font-medium text-gray-600">KLW-BLK</span> or a product
                                        name
                                    </p>
                                    <p className="mt-1 text-xs text-gray-300">
                                        Only catalog products without an existing product BOM will be shown
                                    </p>
                                </div>
                            ) : null}
                        </div>
                    </>
                ) : null}

                {showStep2 ? (
                    <>
                        <div className="flex-shrink-0 border-b border-gray-200 px-5 pb-4 pt-5 pr-12">
                            <div className="mb-2 flex flex-wrap items-center gap-2">
                                <button
                                    type="button"
                                    onClick={() => setStep(1)}
                                    className="flex items-center gap-1 text-xs text-gray-400 transition-colors hover:text-gray-700"
                                >
                                    <ArrowLeftIcon className="h-3 w-3" />
                                    Back
                                </button>
                                <span className="text-gray-200">·</span>
                                <span className="inline-flex items-center rounded-md border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-xs font-medium uppercase tracking-wider text-emerald-700">
                                    Product BOM
                                </span>
                            </div>
                            <h2 className="flex flex-wrap items-center gap-2 text-[14px] font-semibold text-gray-900">
                                {hasSingleSelectedProduct ? (
                                    <>
                                        <SupplySku sku={singleTargetSku} />
                                        {singleTargetName ? (
                                            <span className="text-[13px] font-normal text-gray-400">{singleTargetName}</span>
                                        ) : null}
                                    </>
                                ) : (
                                    <span className="rounded-md border border-blue-200 bg-blue-50 px-2 py-0.5 text-[13px] font-semibold text-blue-700">
                                        {selectedProducts.length} SKUs
                                    </span>
                                )}
                            </h2>
                            <p className="mt-0.5 text-sm text-gray-500">
                                Select components from the library — description, qty, and cost auto-fill.
                            </p>
                        </div>

                        <div className="min-h-0 flex-1">
                            <div className="border-b border-gray-100 bg-gray-50/40 px-5 py-4">
                                <div className="mb-3 flex items-center justify-between">
                                    <p className="text-xs font-semibold uppercase tracking-widest text-gray-400">
                                        Product Details
                                    </p>
                                    <span className="text-xs italic text-gray-400">
                                        {hasSingleSelectedProduct ? "Set in catalog — view only" : `${selectedProducts.length} targets`}
                                    </span>
                                </div>
                                {hasSingleSelectedProduct ? (
                                <div className="grid grid-cols-2 gap-3">
                                    <div className="col-span-2">
                                        <label className="mb-1 block text-xs text-gray-400">Product Name</label>
                                        <div className="flex py-1 items-center rounded-md border border-gray-100 bg-gray-50 px-2.5 text-sm font-medium text-gray-700">
                                            {singleTargetName || <span className="font-normal text-gray-300">—</span>}
                                        </div>
                                    </div>
                                    <div>
                                        <label className="mb-1 block text-xs text-gray-400">Category</label>
                                        <div className="flex h-8 items-center rounded-md border border-gray-100 bg-gray-50 px-2.5 text-sm text-gray-500">
                                            {singleTargetCategory || <span className="text-gray-300">—</span>}
                                        </div>
                                    </div>
                                    <div>
                                        <label className="mb-1 block text-xs text-gray-400">Status</label>
                                        {singleTargetStatusCode ? (
                                            <CatalogStatusChip
                                                status={singleTargetStatusCode}
                                                label={catalogStatusLabel(singleTargetStatusCode)}
                                            />
                                        ) : (
                                            <span className="text-gray-300">—</span>
                                        )}
                                    </div>
                                    <div>
                                        <label className="mb-1 block text-xs text-gray-400">Primary Supplier</label>
                                        <div className="flex h-8 items-center truncate rounded-md border border-gray-100 bg-gray-50 px-2.5 text-sm text-gray-500">
                                            {singleTargetSupplier || <span className="text-gray-300">—</span>}
                                        </div>
                                    </div>
                                    <div>
                                        <label className="mb-1 block text-xs text-gray-400">Lead Time</label>
                                        <div className="flex h-8 items-center rounded-md border border-gray-100 bg-gray-50 px-2.5 text-sm text-gray-500">
                                            {singleTargetLeadTime ? `${singleTargetLeadTime}d` : <span className="text-gray-300">—</span>}
                                        </div>
                                    </div>
                                </div>
                                ) : (
                                    <div className="max-h-24 overflow-y-auto rounded-md border border-gray-100 bg-white p-2">
                                        <div className="flex flex-wrap gap-1.5">
                                            {selectedProducts.map((product) => (
                                                <button
                                                    type="button"
                                                    key={product.id}
                                                    onClick={() =>
                                                        setSelectedProducts((previous) =>
                                                            previous.filter((item) => String(item.id) !== String(product.id))
                                                        )
                                                    }
                                                    className="inline-flex items-center gap-1 px-1 py-1"
                                                >
                                                    <SupplySku sku={product.supplySku || product.sku || product.id} />
                                                    <span>×</span>
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div className="px-5 py-4" ref={dropdownRef}>
                                <CopyBomPicker
                                    products={productBomRows}
                                    excludeProductIds={selectedProductIds}
                                    selectedProductId={copySourceId}
                                    onSelect={handleCopySource}
                                />
                                {copySourceId ? (
                                    <p className="mt-2 text-xs font-medium text-blue-600">
                                        BOM copied from the selected source. Adjust any line below before creating the new BOMs.
                                    </p>
                                ) : null}
                                <div className="mb-3 flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                        <p className="text-xs font-semibold uppercase tracking-widest text-gray-400">
                                            Components
                                        </p>
                                        {newComponentCount > 0 ? (
                                            <span className="inline-flex items-center gap-1 rounded-md border border-amber-200 bg-amber-50 px-1.5 py-0.5 text-xs text-amber-600">
                                                <SparklesIcon className="h-2.5 w-2.5" />
                                                {newComponentCount} new — will save to library
                                            </span>
                                        ) : null}
                                    </div>
                                    <button
                                        type="button"
                                        onClick={addLine}
                                        className="inline-flex h-7 items-center gap-1 rounded-md border border-gray-200 bg-white px-2.5 text-xs text-gray-600 hover:bg-gray-50"
                                    >
                                        <PlusIcon className="h-3 w-3" />
                                        Add Component
                                    </button>
                                </div>

                                <div className="space-y-2">
                                    {lines.map((line) => (
                                        <div
                                            key={line.id}
                                            className="rounded-md border border-gray-200 bg-white p-2.5 transition-colors hover:border-gray-300"
                                        >
                                            <div
                                                className="grid items-center gap-x-2"
                                                style={{ gridTemplateColumns: "1fr 80px 80px 72px 28px" }}
                                            >
                                                <div className="relative min-w-0">
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            setOpenDropdown(openDropdown === line.id ? null : line.id);
                                                            setDropdownSearch("");
                                                        }}
                                                        className={`flex h-7 w-full items-center justify-between gap-1 rounded-md border px-2 text-left text-sm transition-colors focus:outline-none focus:ring-1 focus:ring-gray-400 ${
                                                            line.isNewComponent
                                                                ? "border-amber-300 bg-amber-50/30"
                                                                : "border-gray-200 bg-white hover:border-gray-300"
                                                        }`}
                                                    >
                                                        <span
                                                            className={
                                                                line.componentName
                                                                    ? "truncate text-gray-900"
                                                                    : "truncate text-gray-400"
                                                            }
                                                        >
                                                            {line.componentName || "Select component…"}
                                                        </span>
                                                        <ChevronDownIcon className="h-3 w-3 shrink-0 text-gray-400" />
                                                    </button>

                                                    {openDropdown === line.id ? (
                                                        <div className="absolute left-0 right-0 top-full z-[100] mt-1 overflow-hidden rounded-md border border-gray-200 bg-white shadow-xl">
                                                            <div className="border-b border-gray-100 p-2">
                                                                <input
                                                                    autoFocus
                                                                    value={dropdownSearch}
                                                                    onChange={(e) => setDropdownSearch(e.target.value)}
                                                                    placeholder="Search components…"
                                                                    className="h-7 w-full rounded-md border border-gray-200 px-2 text-xs outline-none focus:border-gray-400"
                                                                />
                                                            </div>
                                                            <div className="max-h-64 overflow-y-auto">
                                                                {filteredBoxPresets.length > 0 ? (
                                                                    <>
                                                                        <div className="flex items-center gap-1.5 border-b border-gray-200 bg-gray-50 px-3 py-1.5">
                                                                            <CubeIcon className="h-3 w-3 text-gray-400" />
                                                                    <span className="text-xs font-semibold uppercase tracking-wider text-gray-400">
                                                                                Box Presets
                                                                            </span>
                                                                        </div>
                                                                        {filteredBoxPresets.map((preset) => (
                                                                            <button
                                                                                key={preset.id}
                                                                                type="button"
                                                                                onClick={() => selectBoxPreset(line.id, preset)}
                                                                                className="flex w-full items-start gap-2 border-b border-gray-100 px-3 py-2 text-left transition-colors last:border-0 hover:bg-blue-50"
                                                                            >
                                                                                <div className="min-w-0 flex-1">
                                                                                    <span className="block truncate text-sm text-gray-900">
                                                                                        {preset.name}
                                                                                    </span>
                                                                                    <span className="block truncate text-xs text-gray-400">
                                                                                        {(preset.components || []).length}{" "}
                                                                                        components · $
                                                                                        {Number(preset.totalCost || 0).toFixed(2)}
                                                                                    </span>
                                                                                </div>
                                                                                <div className="mt-0.5 flex shrink-0 items-center gap-1">
                                                                                    {preset.source === "group" ? (
                                                                                        <span className="text-xs font-medium text-gray-400">
                                                                                            Inherited
                                                                                        </span>
                                                                                    ) : null}
                                                                                        <span className="text-xs font-medium text-blue-600">
                                                                                        Preset
                                                                                    </span>
                                                                                </div>
                                                                            </button>
                                                                        ))}
                                                                    </>
                                                                ) : null}
                                                                {filteredLibrary.length > 0 ? (
                                                                    <>
                                                                        {filteredBoxPresets.length > 0 ? (
                                                                        <div className="border-b border-gray-200 bg-gray-50 px-3 py-1.5">
                                                                                <span className="text-xs font-semibold uppercase tracking-wider text-gray-400">
                                                                                Component Presets
                                                                                </span>
                                                                            </div>
                                                                        ) : null}
                                                                        {filteredLibrary.map((comp) => (
                                                                            <button
                                                                                key={comp.id}
                                                                                type="button"
                                                                                onClick={() => selectLibraryComponent(line.id, comp)}
                                                                                className="flex w-full items-start gap-2 border-b border-gray-100 px-3 py-2 text-left transition-colors last:border-0 hover:bg-gray-50"
                                                                            >
                                                                                <div className="min-w-0 flex-1">
                                                                                    <span className="block truncate text-sm text-gray-900">
                                                                                        {comp.name}
                                                                                    </span>
                                                                                    <span className="block truncate text-xs text-gray-400">
                                                                                    {comp.type || comp.notes || comp.defaultSupplier || ""}
                                                                                        {Number(comp.defaultUnitCost) > 0
                                                                                            ? ` · $${Number(comp.defaultUnitCost).toFixed(2)}`
                                                                                            : ""}
                                                                                    </span>
                                                                                </div>
                                                                            </button>
                                                                        ))}
                                                                    </>
                                                                ) : null}
                                                                {filteredBoxPresets.length === 0 &&
                                                                filteredLibrary.length === 0 &&
                                                                !dropdownSearch.trim() ? (
                                                                    <div className="px-3 py-3 text-sm text-gray-400">
                                                                        Add components or box presets in Settings.
                                                                    </div>
                                                                ) : null}
                                                            </div>
                                                        </div>
                                                    ) : null}
                                                </div>
                                                <input
                                                    type="number"
                                                    min={0}
                                                    value={line.qty}
                                                    onChange={(e) =>
                                                        updateLine(line.id, { qty: parseFloat(e.target.value) || 0 })
                                                    }
                                                    className="h-7 rounded-md border border-gray-200 px-2 text-right text-sm outline-none focus:border-gray-400 [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                                                />
                                                <input
                                                    type="number"
                                                    min={0}
                                                    step={0.01}
                                                    value={line.unitCost}
                                                    onChange={(e) =>
                                                        updateLine(line.id, { unitCost: parseFloat(e.target.value) || 0 })
                                                    }
                                                    className="h-7 rounded-md border border-gray-200 px-2 text-right text-sm outline-none focus:border-gray-400 [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                                                />
                                                <span className="text-right text-sm tabular-nums text-gray-700">
                                                    {formatMoney(line.extendedCost)}
                                                </span>
                                                <button
                                                    type="button"
                                                    onClick={() => removeLine(line.id)}
                                                    className="flex items-center justify-center text-gray-300 hover:text-red-400"
                                                    title="Remove"
                                                >
                                                    <TrashIcon className="h-3.5 w-3.5" />
                                                </button>
                                            </div>

                                            <div className="mt-1.5 grid grid-cols-2 gap-x-2">
                                                <input
                                                    value={line.description}
                                                    onChange={(e) => updateLine(line.id, { description: e.target.value })}
                                                    placeholder="Description / notes"
                                                    className="py-0.5 rounded-md border border-gray-100 bg-gray-50 px-2 text-xs text-gray-800 placeholder:text-gray-300 outline-none"
                                                />
                                                <AddDrawerSelect
                                                    options={vendorOptions || []}
                                                    value={line.vendorId ?? ""}
                                                    onChange={(value) =>
                                                        updateLine(line.id, {
                                                            vendorId:
                                                                value === undefined || value === null ? "" : String(value),
                                                        })
                                                    }
                                                    noneLabel="Select supplier…"
                                                    allLabel="All suppliers"
                                                    isLoading={vendorSelectLoading}
                                                    disabled={vendorSelectLoading}
                                                    stackKey={PRODUCT_BOM_SUPPLIER_STACK(line.id)}
                                                    openStackKey={openSupplierSelectKey}
                                                    onStackKeyChange={setOpenSupplierSelectKey}
                                                    selectClassname="w-full"
                                                    buttonSelectedClassname="!py-0.5 !bg-gray-50 !border-gray-100"
                                                />
                                            </div>
                                        </div>
                                    ))}

                                    {lines.length === 0 ? (
                                        <div className="flex flex-col items-center justify-center rounded-md border border-dashed border-gray-200 py-8 text-gray-300">
                                            <span className="text-sm">No components</span>
                                        </div>
                                    ) : null}
                                </div>
                            </div>
                        </div>
                    </>
                ) : null}
            </div>
        </Drawer>
    );
}

const PRODUCT_COLUMNS = [
    {
        key: "supplySku",
        label: "Supply SKU",
        render: (row) => bomColumnRenderers.sku(row.supplySku),
    },
    {
        key: "canonicalName",
        label: "Product / Name",
        render: (row) => (
            <div className="min-w-0">
                <div className="truncate text-sm font-medium text-gray-900">{row.canonicalName || "—"}</div>
                {row.variantName && row.variantName !== "—" ? (
                    <div className="truncate text-xs text-gray-400">{row.variantName}</div>
                ) : null}
            </div>
        ),
    },
    {
        key: "category",
        label: "Category",
        render: (row) => (
            <div className="min-w-0">
                <div className="truncate text-sm text-gray-700">{row.category || "—"}</div>
                {row.subcategory && row.subcategory !== "—" ? (
                    <div className="truncate text-xs text-gray-400">{row.subcategory}</div>
                ) : null}
            </div>
        ),
    },
    {
        key: "qty",
        label: "Qty",
        align: "right",
        render: (row) => (
            <span className="text-sm text-gray-500">
                {Number(row.componentCount || row.qty || 0)} <span className="text-xs text-gray-400">parts</span>
            </span>
        ),
    },
    {
        key: "unitCostTotal",
        label: "Unit Cost",
        align: "right",
        render: () => <span className="text-xs text-gray-300">—</span>,
    },
    {
        key: "extendedCost",
        label: "Ext. Cost",
        align: "right",
        render: () => <span className="text-xs text-gray-300">—</span>,
    },
    { key: "supplier", label: "Supplier", additionalClassname: "min-w-[230px]" },
    { key: "leadTimeDays", label: "Lead Time", align: "right", render: (row) => (row.leadTimeDays ? `${row.leadTimeDays}d` : "—") },
    { key: "totalBomCost", label: "Total BOM Cost", align: "right", type: "money" },
    { key: "status", label: "Status", render: (row) => bomColumnRenderers.status(row.status) },
];

const PRODUCT_CHILD_COLUMNS = [
    { key: "componentName", label: "Name" },
    {
        key: "description",
        label: "Description",
        render: (row) => (row.description ? <span className="text-gray-600">{row.description}</span> : "—"),
    },
    { key: "componentType", label: "-", render: () => "—" },
    { key: "qty", label: "Qty", align: "right" },
    { key: "unitCost", label: "Unit Cost", align: "right", type: "money" },
    { key: "extendedCost", label: "Ext. Cost", align: "right", type: "money" },
    {
        key: "supplier",
        label: "Supplier",
        render: (row) => {
            const s = String(row.supplier || "").trim();
            if (!s || s === "—") {
                return <span className="text-gray-400">—</span>;
            }
            return <span className="text-sm text-gray-700">{s}</span>;
        },
    },
];

const ProductBomPanel = ({ onMetricsChange }) => {
    const dispatch = useDispatch();
    const { filtersByTab, selectedIdsByTab, expandedIdsByTab, data, loadingKeys = [] } = useSelector(selectBomUi);
    const { vendorsToSelect, loadingKeys: groupLoadingKeys } = useSelector((state) => state.productGroup);
    const categorySettingsItems = useSelector((state) => state.productCatalog?.categorySettingsItems || []);
    const filters = filtersByTab.product;
    const selectedIds = selectedIdsByTab.product;
    const expandedIds = expandedIdsByTab.product;
    const items = data.products || [];
    const componentLibrary = useMemo(
        () =>
            [...(data.componentLibrary || [])].sort((a, b) =>
                String(a.name || "").localeCompare(String(b.name || ""), undefined, { sensitivity: "base" })
            ),
        [data.componentLibrary]
    );
    const boxPresets = useMemo(() => {
        const localItems = (data.boxPresets || []).map((item) => ({
            ...item,
            groupId: item.groupId ?? null,
            source: "settings",
        }));
        const groupItems = (data.packagingBoxPresetOptions || []).map((item) => ({
            ...item,
            groupId: item.groupId ?? null,
            source: "group",
        }));
        return [...groupItems, ...localItems].sort((a, b) =>
            String(a.name || "").localeCompare(String(b.name || ""), undefined, { sensitivity: "base" })
        );
    }, [data.boxPresets, data.packagingBoxPresetOptions]);
    const [addDrawerOpen, setAddDrawerOpen] = useState(false);
    const [editProduct, setEditProduct] = useState(null);
    const [savingProductBom, setSavingProductBom] = useState(false);
    const [deleteBomTarget, setDeleteBomTarget] = useState(null);
    const isLoading = loadingKeys.includes(BomUiLoadingKeys.fetchProductUi);
    const deleteBomLoading = loadingKeys.includes(BomUiLoadingKeys.deleteAllProductBom);

    const vendorOptions = useMemo(
        () =>
            (vendorsToSelect || []).map((vendor) => ({
                value: String(vendor.value),
                label: vendor.label,
            })),
        [vendorsToSelect]
    );
    const vendorSelectLoading = groupLoadingKeys.includes(ProductGroupLoadingKeys.getSuppliers);
    const selectedRows = useMemo(
        () => items.filter((item) => selectedIds.includes(item.id)),
        [items, selectedIds]
    );
    const selectedComponentOptions = useMemo(() => {
        const map = new Map();
        selectedRows.forEach((row) => {
            (row.components || []).forEach((component) => {
                const name = String(component?.componentName || component?.component || "").trim();
                if (!name) return;
                const key = name.toLowerCase();
                const current = map.get(key) || {
                    value: name,
                    label: name,
                    qtyValues: new Set(),
                };
                current.qtyValues.add(Number(component?.qty ?? 0));
                map.set(key, current);
            });
        });
        return Array.from(map.values())
            .map((item) => {
                const qtyValues = Array.from(item.qtyValues);
                return {
                    value: item.value,
                    label: item.label,
                    qtyLabel:
                        qtyValues.length === 1
                            ? `qty ${qtyValues[0]}`
                            : qtyValues.length > 1
                              ? "mixed qty"
                              : "",
                };
            })
            .sort((a, b) => String(a.label).localeCompare(String(b.label), undefined, { sensitivity: "base" }));
    }, [selectedRows]);

    useEffect(() => {
        dispatch(getSuppliers({}));
    }, [dispatch]);

    useEffect(() => {
        dispatch(fetchBomComponentLibrary());
    }, [dispatch]);

    useEffect(() => {
        dispatch(fetchProductCatalogCategorySettings());
    }, [dispatch]);

    const filteredRows = useMemo(() => {
        const search = String(filters.search || "").trim().toLowerCase();
        return items.filter((item) => {
            const matchesSearch =
                !search ||
                [item.supplySku, item.canonicalName, item.productName, item.variantName, item.category, item.subcategory, item.supplier]
                    .filter(Boolean)
                    .join(" ")
                    .toLowerCase()
                    .includes(search);
            const matchesProductName = filters.productName === "all" || item.canonicalName === filters.productName;
            return matchesSearch && matchesProductName;
        });
    }, [filters, items]);
    const isFiltered = useMemo(() => {
        const hasSearch = String(filters?.search || "").trim().length > 0;
        const hasProductSelect = String(filters?.productName || "all") !== "all";
        return hasSearch || hasProductSelect;
    }, [filters?.productName, filters?.search]);

    const productOptions = useMemo(
        () =>
            Array.from(new Set(items.map((item) => item.canonicalName).filter(Boolean))).map((value) => ({
                value,
                label: value,
            })),
        [items]
    );

    const allExpanded = filteredRows.length > 0 && filteredRows.every((row) => expandedIds.includes(row.id));

    useEffect(() => {
        onMetricsChange?.({
            totalRows: filteredRows.length,
            selectedRows: selectedIds.length,
            totalCost: filteredRows.reduce((sum, row) => sum + Number(row.totalBomCost || 0), 0),
        });
    }, [filteredRows, onMetricsChange, selectedIds.length]);

    const applyBulkComponentMutation = async ({
        mutateComponents,
        successMessage,
        emptyMessage = "No component changes to apply",
    }) => {
        if (savingProductBom || selectedRows.length === 0) return;

        setSavingProductBom(true);
        try {
            let changedRows = 0;

            for (const row of selectedRows) {
                const productId = String(row?.id || "").trim();
                if (!productId) continue;

                const nextComponents = mutateComponents(row);
                const currentPayload = serializeProductBomComponents(row.components || []);
                const nextPayload = serializeProductBomComponents(nextComponents || []);

                if (JSON.stringify(currentPayload) === JSON.stringify(nextPayload)) {
                    continue;
                }

                await dispatch(
                    saveProductBom({
                        productId,
                        components: nextPayload,
                    })
                ).unwrap();

                changedRows += 1;
            }

            if (!changedRows) {
                toast.info(emptyMessage, { position: "bottom-right" });
                return;
            }

            await dispatch(fetchProductBomUi()).unwrap();

            toast.success(
                typeof successMessage === "function" ? successMessage(changedRows) : successMessage,
                { position: "bottom-right" }
            );
        } catch (error) {
            const message =
                typeof error === "string" ? error : error?.message || "Failed to update selected BOM rows";
            toast.error(String(message), { position: "bottom-right" });
        } finally {
            setSavingProductBom(false);
        }
    };

    const handleBulkAddComponent = async (draftLine) => {
        const componentName = String(draftLine?.componentName || "").trim();
        if (!componentName) return;

        await applyBulkComponentMutation({
            mutateComponents: (row) => [
                ...(row.components || []),
                createBulkDraftComponentLine(draftLine, row.id),
            ],
            successMessage: (changedRows) =>
                `Added "${componentName}" to ${changedRows} BOM${changedRows === 1 ? "" : "s"}`,
        });
    };

    const handleCreateBulkComponentLibraryItem = async (draft) => {
        const created = await dispatch(
            createBomComponentLibraryItem({
                component: String(draft?.component || "").trim(),
                description: String(draft?.description || "").trim() || null,
                qty: Number(draft?.qty || 1),
                cost: Number(draft?.cost || 0),
                notes: draft?.notes ?? null,
                vendorId: draft?.vendorId ? String(draft.vendorId).trim() : null,
            })
        ).unwrap();
        return created?.item || null;
    };

    const handleBulkRemoveComponent = async (componentName) => {
        const normalizedName = String(componentName || "").trim().toLowerCase();
        if (!normalizedName) return;

        await applyBulkComponentMutation({
            mutateComponents: (row) =>
                (row.components || []).filter(
                    (component) =>
                        String(component?.componentName || component?.component || "").trim().toLowerCase() !==
                        normalizedName
                ),
            successMessage: (changedRows) =>
                `Removed "${componentName}" from ${changedRows} BOM${changedRows === 1 ? "" : "s"}`,
            emptyMessage: `Component "${componentName}" was not found in the selected BOMs`,
        });
    };

    const handleBulkUpdateQty = async (componentName, qty) => {
        const normalizedName = String(componentName || "").trim().toLowerCase();
        if (!normalizedName) return;

        await applyBulkComponentMutation({
            mutateComponents: (row) =>
                (row.components || []).map((component) => {
                    const currentName = String(component?.componentName || component?.component || "")
                        .trim()
                        .toLowerCase();
                    if (currentName !== normalizedName) {
                        return component;
                    }

                    const nextQty = Number(qty) || 0;
                    const unitCost = Number(component?.unitCost ?? component?.cost ?? 0) || 0;
                    return {
                        ...component,
                        qty: nextQty,
                        extendedCost: Math.round(nextQty * unitCost * 10000) / 10000,
                    };
                }),
            successMessage: (changedRows) =>
                `Updated "${componentName}" qty to ${Number(qty) || 0} in ${changedRows} BOM${changedRows === 1 ? "" : "s"}`,
            emptyMessage: `Component "${componentName}" was not found in the selected BOMs`,
        });
    };

    const handleBulkApplyBoxPreset = async (preset) => {
        const presetName = String(preset?.name || "").trim();
        if (!presetName) return;

        await applyBulkComponentMutation({
            mutateComponents: (row) => {
                const existingComponents = (row.components || []).filter((component) => {
                    const name = String(component?.componentName || component?.component || "").trim().toLowerCase();
                    return !BULK_BOX_PRESET_REPLACED_COMPONENTS.has(name);
                });

                return [...existingComponents, ...createBoxPresetLines(preset)];
            },
            successMessage: (changedRows) =>
                `Applied "${presetName}" to ${changedRows} BOM${changedRows === 1 ? "" : "s"}`,
        });
    };

    const handleBulkCopyBom = async ({ sourceProductId, mode = "replace" }) => {
        const source = items.find((row) => String(row.id) === String(sourceProductId));
        const targetIds = selectedRows
            .map((row) => String(row.id || "").trim())
            .filter((id) => id && id !== String(sourceProductId));
        if (!source || !targetIds.length) {
            toast.info("Select at least one target product other than the source", { position: "bottom-right" });
            return;
        }

        setSavingProductBom(true);
        try {
            await dispatch(
                saveProductBomBulk({
                    productIds: targetIds,
                    components: serializeProductBomComponents(source.components || []),
                    mode,
                })
            ).unwrap();
            await dispatch(fetchProductBomUi()).unwrap();
            toast.success(
                `Copied ${source.components?.length || 0} components to ${targetIds.length} BOM${targetIds.length === 1 ? "" : "s"}`,
                { position: "bottom-right" }
            );
        } catch (error) {
            const message = typeof error === "string" ? error : error?.message || "Failed to copy product BOM";
            toast.error(message, { position: "bottom-right" });
        } finally {
            setSavingProductBom(false);
        }
    };

    const handleProductSave = async (item, { newComponents = [] } = {}) => {
        setSavingProductBom(true);
        try {
            const createdPresetIds = new Map();
            for (let i = 0; i < newComponents.length; i += 1) {
                const l = newComponents[i];
                const name = String(l.componentName || "").trim();
                if (!name) continue;
                const created = await dispatch(
                    createBomComponentLibraryItem({
                        component: name,
                        description: String(l.description || l.componentType || "").trim() || null,
                        qty: Number(l.qty || 1),
                        cost: Number(l.unitCost || 0),
                        notes: null,
                        vendorId: l.vendorId ? String(l.vendorId).trim() : null,
                    })
                ).unwrap();
                if (created?.item?.id != null) {
                    createdPresetIds.set(String(l.id), String(created.item.id));
                }
            }
            if (newComponents.length > 0) {
                toast.success(
                    `${newComponents.length} new component preset${newComponents.length > 1 ? "s" : ""} saved`,
                    { position: "bottom-right" }
                );
            }

            const normalizedItem = createdPresetIds.size
                ? {
                      ...item,
                      components: (item.components || []).map((component) => {
                          const presetId = createdPresetIds.get(String(component.id));
                          if (!presetId) return component;
                          return {
                              ...component,
                              componentPresetId: presetId,
                          };
                      }),
                  }
                : item;

            const productIds = Array.from(
                new Set(
                    (Array.isArray(normalizedItem.productIds) ? normalizedItem.productIds : [normalizedItem.id])
                        .map((id) => String(id || "").trim())
                        .filter(Boolean)
                )
            );
            const isUpdate = normalizedItem.id && items.some((row) => row.id === normalizedItem.id);
            const productId = String(normalizedItem.id || "").trim();
            const canPersist = Boolean(productId) && !productId.startsWith("product-");

            const isBulkProductSave = Array.isArray(normalizedItem.productIds) && !normalizedItem.id;
            if (isBulkProductSave) {
                const persistableProductIds = productIds.filter((id) => !id.startsWith("product-"));
                if (!persistableProductIds.length) {
                    const error = new Error("No persistable catalog products are selected");
                    toast.error(error.message, { position: "bottom-right" });
                    throw error;
                }
                try {
                    await dispatch(
                        saveProductBomBulk({
                            productIds: persistableProductIds,
                            components: serializeProductBomComponents(normalizedItem.components || []),
                            mode: "replace",
                        })
                    ).unwrap();
                    await dispatch(fetchProductBomUi()).unwrap();
                    toast.success(`Created BOM for ${persistableProductIds.length} SKUs`, { position: "bottom-right" });
                } catch (error) {
                    const message =
                        typeof error === "string" ? error : error?.message || "Failed to create product BOMs";
                    toast.error(message, { position: "bottom-right" });
                    throw error;
                }
                return;
            }

            if (canPersist) {
                try {
                    if (normalizedItem.persistCatalogWithBom) {
                        await dispatch(
                            updateProductCatalogProductSettings({
                                productId,
                                status: normalizedItem.inventoryStatusCode,
                                category_group_id:
                                    normalizedItem.category_group_id != null && normalizedItem.category_group_id !== ""
                                        ? Number(normalizedItem.category_group_id)
                                        : null,
                                subcategory_variant_id:
                                    normalizedItem.subcategory_variant_id != null && normalizedItem.subcategory_variant_id !== ""
                                        ? Number(normalizedItem.subcategory_variant_id)
                                        : null,
                                lead_time_days: Number(normalizedItem.leadTimeDays) || 0,
                                skipSuccessToast: true,
                            })
                        ).unwrap();

                        await dispatch(
                            updateVendorByProduct({
                                productId,
                                newVendor:
                                    normalizedItem.vendor_id != null && String(normalizedItem.vendor_id).trim() !== ""
                                        ? String(normalizedItem.vendor_id).trim()
                                        : null,
                                skipSuccessToast: true,
                            })
                        ).unwrap();
                    }

                    await dispatch(
                        saveProductBom({
                            productId,
                            components: serializeProductBomComponents(normalizedItem.components || []),
                        })
                    ).unwrap();
                    await dispatch(fetchProductBomUi()).unwrap();
                    toast.success(
                        normalizedItem.persistCatalogWithBom
                            ? `Product BOM and product details for "${normalizedItem.supplySku}" saved`
                            : isUpdate
                              ? `Product BOM "${normalizedItem.supplySku}" saved`
                              : `Product BOM "${normalizedItem.supplySku}" created`,
                        { position: "bottom-right" }
                    );
                } catch (error) {
                    const message =
                        typeof error === "string" ? error : error?.message || "Failed to save product BOM";
                    toast.error(message, { position: "bottom-right" });
                    throw error;
                }
                return;
            }

            dispatch(isUpdate ? updateBomProduct(normalizedItem) : addBomProduct(normalizedItem));
            if (!isUpdate) {
                toast.success(`Product BOM "${normalizedItem.supplySku}" created`, { position: "bottom-right" });
            }
        } finally {
            setSavingProductBom(false);
        }
    };

    const deleteBomDisplayLabel = deleteBomTarget
        ? String(deleteBomTarget.supplySku || "").trim() ||
          String(deleteBomTarget.id || "").trim() ||
          "this product"
        : "";

    const confirmDeleteProductBom = async () => {
        if (!deleteBomTarget) return;
        const row = deleteBomTarget;
        const label =
            String(row.supplySku || "").trim() || String(row.id || "").trim() || "this product";
        try {
            await dispatch(deleteAllProductBom(row.id)).unwrap();
            toast.success(`All components removed for ${label}`, { position: "bottom-right" });
            setDeleteBomTarget(null);
        } catch (error) {
            const message =
                typeof error === "string"
                    ? error
                    : error?.error || error?.message || "Failed to delete BOM components";
            toast.error(message, { position: "bottom-right" });
        }
    };

    return (
        <div className="flex h-full min-h-0 flex-1 flex-col">
            <section>
                <div className="flex items-center gap-3 rounded-lg border border-gray-200 bg-white shadow-sm px-5 py-3.5 m-4 mb-0">
                    <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md bg-gray-100">
                        <CubeIcon className="h-4 w-4 text-gray-600" />
                    </div>
                    <div className="flex items-center gap-2">
                        <span className="text-[22px] font-semibold text-gray-900 leading-none tracking-tight">
                            {isFiltered ? filteredRows.length : items.length}
                        </span>
                        <span className="text-sm text-gray-500">
                            {isFiltered ? "SKUs matching filters" : "SKUs with BOMs"}
                        </span>
                        {isFiltered ? (
                            <span className="inline-flex items-center rounded-md border border-gray-200 bg-gray-50 px-1.5 py-0.5 text-xs font-medium text-gray-500">
                                filtered
                            </span>
                        ) : null}
                    </div>
                </div>
            </section>
            <BomToolbar
                filters={filters}
                onFilterChange={(key, value) => dispatch(setBomFilter({ tab: "product", key, value }))}
                onClearFilters={() => dispatch(clearBomFilters("product"))}
                secondaryOptions={productOptions}
                secondaryPlaceholder="All Products"
                secondaryFilterKey="productName"
                canToggleExpand
                allExpanded={allExpanded}
                onToggleExpand={() =>
                    dispatch(
                        setBomExpandedRows({
                            tab: "product",
                            ids: allExpanded ? [] : filteredRows.map((row) => row.id),
                        })
                    )
                }
                addLabel="Add BOM"
                onAdd={() => {
                    setEditProduct(null);
                    setAddDrawerOpen(true);
                }}
                onExport={() =>
                    downloadCsv("bom-products.csv", [
                        ["Supply SKU", "Product / Name", "Category", "Qty", "Unit Cost", "Ext. Cost", "Supplier", "Lead Time", "Total BOM Cost", "Status"],
                        ...filteredRows.map((row) => [
                            row.supplySku,
                            row.canonicalName,
                            row.category,
                            row.qty,
                            row.unitCostTotal,
                            row.extendedCost,
                            row.supplier,
                            row.leadTimeDays,
                            row.totalBomCost,
                            row.status,
                        ]),
                    ])
                }
            />
            <BomBulkBar
                selectedCount={selectedIds.length}
                componentLibrary={componentLibrary}
                componentOptions={selectedComponentOptions}
                boxPresets={boxPresets}
                vendorOptions={vendorOptions}
                vendorSelectLoading={vendorSelectLoading}
                busy={savingProductBom}
                onApplyAddComponent={handleBulkAddComponent}
                onCreateComponentLibraryItem={handleCreateBulkComponentLibraryItem}
                onOpenComponentLibrary={() => dispatch(setBomSettingsOpen({ open: true, section: "components" }))}
                onRemoveComponent={handleBulkRemoveComponent}
                onUpdateComponentQty={handleBulkUpdateQty}
                onApplyBoxPreset={handleBulkApplyBoxPreset}
                productRows={items}
                onCopyBom={handleBulkCopyBom}
                onClear={() => dispatch(clearBomRowSelection("product"))}
            />
            <div className="min-h-0 flex-1 p-1 px-4">
                {isLoading ? (
                    <div className="flex h-full min-h-[320px] items-center justify-center rounded-lg border border-gray-200 bg-white shadow-sm">
                        <LoaderSpinner width="36" height="36" border="3" colorSpinner="black" />
                    </div>
                ) : (
                    <BomTable
                        rows={filteredRows}
                        columns={PRODUCT_COLUMNS}
                        childColumns={PRODUCT_CHILD_COLUMNS}
                        expandedIds={expandedIds}
                        selectedIds={selectedIds}
                        onToggleRow={(row) => dispatch(toggleBomExpandedRow({ tab: "product", id: row.id }))}
                        onEditRow={(row) => {
                            setAddDrawerOpen(false);
                            setEditProduct(row);
                        }}
                        onDeleteRow={(row) => setDeleteBomTarget(row)}
                        onToggleSelect={(row) => dispatch(toggleBomRowSelection({ tab: "product", id: row.id }))}
                        onToggleSelectAll={() =>
                            dispatch(
                                setBomRowSelection({
                                    tab: "product",
                                    ids:
                                        filteredRows.length &&
                                        filteredRows.every((row) => selectedIds.includes(row.id))
                                            ? []
                                            : filteredRows.map((row) => row.id),
                                })
                            )
                        }
                        emptyTitle="No product BOM rows yet"
                        emptyDescription="No active products with product BOM rows were found."
                        emptyActionLabel="Add Product BOM"
                        onEmptyAction={() => {
                            setEditProduct(null);
                            setAddDrawerOpen(true);
                        }}
                    />
                )}
            </div>
            <ProductBomDrawer
                isOpen={addDrawerOpen}
                onClose={() => setAddDrawerOpen(false)}
                componentLibrary={componentLibrary}
                boxPresets={boxPresets}
                onSave={handleProductSave}
                vendorOptions={vendorOptions}
                vendorSelectLoading={vendorSelectLoading}
                saveBusy={savingProductBom}
                productBomRows={items}
            />
            <EditProductBomDrawer
                isOpen={Boolean(editProduct)}
                onClose={() => setEditProduct(null)}
                product={editProduct}
                categorySettingsItems={categorySettingsItems}
                componentLibrary={componentLibrary}
                boxPresets={boxPresets}
                onSave={handleProductSave}
                vendorOptions={vendorOptions}
                vendorSelectLoading={vendorSelectLoading}
                saveBusy={savingProductBom}
                productBomRows={items}
            />
            <ConfrimModal
                isOpen={Boolean(deleteBomTarget)}
                isLoading={deleteBomLoading}
                title={
                    deleteBomTarget
                        ? `Delete all BOM components for "${deleteBomDisplayLabel}"? This removes every line item for this product and cannot be undone.`
                        : ""
                }
                confirmButtonTitle="Delete all components"
                submitClick={confirmDeleteProductBom}
                cancelClick={() => {
                    if (!deleteBomLoading) setDeleteBomTarget(null);
                }}
                size="max-w-lg"
            />
        </div>
    );
};

export default ProductBomPanel;
